import { ExternalLink, Star, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { AffiliatePartner } from '@/hooks/useAffiliatePartners';
import { useAffiliateClicks } from '@/hooks/useAffiliateClicks';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';

interface ComparisonTableProps {
  partners: AffiliatePartner[];
  title?: string;
  description?: string;
}

export const ComparisonTable = ({ 
  partners, 
  title = "Compare Travel Partners",
  description 
}: ComparisonTableProps) => {
  const { mutate: trackClick } = useAffiliateClicks();

  const handleClick = (partner: AffiliatePartner) => {
    trackClick({ partner });
    window.open(partner.affiliate_url, '_blank', 'noopener,noreferrer');
  };

  const renderStars = (rating: number | null) => {
    if (!rating) return <span className="text-muted-foreground text-sm">N/A</span>;
    
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-muted-foreground/30'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="my-12">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold mb-3">{title}</h2>
        {description && (
          <p className="text-muted-foreground max-w-2xl mx-auto">{description}</p>
        )}
      </div>

      {/* Desktop View */}
      <div className="hidden lg:block overflow-x-auto">
        <Card className="border-primary/20">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="p-4 text-left font-semibold">Partner</th>
                <th className="p-4 text-left font-semibold">Rating</th>
                <th className="p-4 text-left font-semibold">Pros</th>
                <th className="p-4 text-left font-semibold">Cons</th>
                <th className="p-4 text-center font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {partners.map((partner, idx) => (
                <tr 
                  key={partner.id} 
                  className={`border-b last:border-0 ${
                    partner.featured ? 'bg-accent/10' : ''
                  }`}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {partner.logo_url && (
                        <Avatar className="h-12 w-12 rounded-md">
                          <AvatarImage src={partner.logo_url} alt={partner.partner_name} />
                          <AvatarFallback>{partner.partner_name[0]}</AvatarFallback>
                        </Avatar>
                      )}
                      <div>
                        <div className="font-semibold flex items-center gap-2">
                          {partner.partner_name}
                          {partner.featured && (
                            <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                              Recommended
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-1">
                          {partner.description}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    {renderStars(partner.star_rating)}
                  </td>
                  <td className="p-4">
                    {partner.pros && partner.pros.length > 0 ? (
                      <ul className="space-y-1">
                        {partner.pros.slice(0, 3).map((pro, idx) => (
                          <li key={idx} className="text-sm text-muted-foreground flex items-start gap-1">
                            <Check className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                            <span className="line-clamp-1">{pro}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-sm text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="p-4">
                    {partner.cons && partner.cons.length > 0 ? (
                      <ul className="space-y-1">
                        {partner.cons.slice(0, 3).map((con, idx) => (
                          <li key={idx} className="text-sm text-muted-foreground flex items-start gap-1">
                            <X className="h-4 w-4 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                            <span className="line-clamp-1">{con}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-sm text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="p-4 text-center">
                    <Button 
                      onClick={() => handleClick(partner)}
                      variant={partner.featured ? 'default' : 'outline'}
                    >
                      {partner.button_text || 'Visit Site'}
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      {/* Mobile View */}
      <div className="lg:hidden space-y-4">
        {partners.map((partner) => (
          <Card 
            key={partner.id} 
            className={`p-4 ${partner.featured ? 'border-primary bg-accent/5' : ''}`}
          >
            <div className="flex items-start gap-3 mb-3">
              {partner.logo_url && (
                <Avatar className="h-12 w-12 rounded-md">
                  <AvatarImage src={partner.logo_url} alt={partner.partner_name} />
                  <AvatarFallback>{partner.partner_name[0]}</AvatarFallback>
                </Avatar>
              )}
              <div className="flex-1">
                <div className="font-semibold flex items-center gap-2 mb-1">
                  {partner.partner_name}
                  {partner.featured && (
                    <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                      Recommended
                    </span>
                  )}
                </div>
                {renderStars(partner.star_rating)}
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground mb-3">{partner.description}</p>
            
            {partner.pros && partner.pros.length > 0 && (
              <div className="mb-3">
                <h4 className="text-sm font-semibold text-green-600 dark:text-green-400 mb-1">
                  ✓ Pros
                </h4>
                <ul className="text-sm space-y-1">
                  {partner.pros.map((pro, idx) => (
                    <li key={idx} className="text-muted-foreground">• {pro}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {partner.cons && partner.cons.length > 0 && (
              <div className="mb-3">
                <h4 className="text-sm font-semibold text-orange-600 dark:text-orange-400 mb-1">
                  ✗ Cons
                </h4>
                <ul className="text-sm space-y-1">
                  {partner.cons.map((con, idx) => (
                    <li key={idx} className="text-muted-foreground">• {con}</li>
                  ))}
                </ul>
              </div>
            )}
            
            <Button 
              onClick={() => handleClick(partner)}
              className="w-full"
              variant={partner.featured ? 'default' : 'outline'}
            >
              {partner.button_text || 'Visit Site'}
              <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </Card>
        ))}
      </div>
    </div>
  );
};
